import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileUp, History, UserPlus, UserRound } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import FilterSelect from "../../../../shared/ui/form/FilterSelect";
import Input from "../../../../shared/ui/form/input/InputField";
import Label from "../../../../shared/ui/form/Label";
import { Modal } from "../../../../shared/ui/modal";
import { Pagination } from "../../../../shared/ui/common/Pagination";
import SubjectKindIcon, { type SubjectKind } from "../../../../shared/ui/icons/SubjectKindIcon";
import { useCenter, useCenterMembers, useCenterPatients, useCenterUnits, useReassignPatient, useReassignUnit, useReassignmentPreview, useCenterCourses, useSessionPreferences, academicYearOptions, formatAcademicYear } from "../../../center";
import type { CenterPatient } from "../../../center";
import { groupByPerson, WHOLE_CASELOAD } from "../../domain/caseloadPeople";
import AddStudentsModal from "../components/AddStudentsModal";
import CenterCoupleEditModal from "../components/CenterCoupleEditModal";
import CenterGroupEditModal from "../components/CenterGroupEditModal";
import CenterPatientDetailModal from "../components/CenterPatientDetailModal";
import StudentImportModal from "../components/StudentImportModal";
import CenterPastSessionsImport from "../components/CenterPastSessionsImport";
import CenterAdminShell from "../components/CenterAdminShell";
import ReassignmentWarnings from "../components/ReassignmentWarnings";
import { centerGroupLabel } from "../centerGroupLabel";

type ListTab = "individual" | "couple" | "group";

/** One line of the caseload: a patient, a couple or a group. */
interface CaseloadRow {
    kind: SubjectKind;
    id: string;
    name: string;
    /** A student's course and year; a couple's or group's members, under its name. */
    detail?: string;
    therapistId: string;
    sessionCount: number;
    lastSessionAt: string | null;
    /** Who is in it, as far as the centre holds them — the patient filter matches on these. */
    memberIds: string[];
    /** The address the centre holds, shown so two records of one person read
     *  as one person. Individuals only. */
    email?: string | null;
    /** Every record the centre holds for this person, when it holds more than
     *  one. Listed rather than summed: separate professionals, separate
     *  therapies. Reassigning acts on one of them, never on "the person". */
    alsoWith?: { patientId: string; therapistId: string; sessionCount: number; lastSessionAt: string | null; detail?: string }[];
}

/** What the API answers when a handover is refused, and what to tell the administrator. */
const REASSIGN_ERRORS: Record<string, string> = {
    patient_in_couple: "center.patients.errorInCouple",
    patient_in_group: "center.patients.errorInGroup",
    therapist_not_in_center: "center.patients.errorNotInCenter",
    handover_splits_a_patient: "center.patients.errorSplits",
    couple_not_fully_in_center: "center.patients.errorNotFullyInCenter",
    group_not_fully_in_center: "center.patients.errorNotFullyInCenter",
    handover_slot_taken: "center.patients.errorSlotTaken",
};

/** Rows per page of the caseload table. */
const PAGE_SIZE = 25;
// The row's action cell, kept in view at the table's right edge.
const ACTION_CELL = "sticky right-0 bg-surface-raised pl-3";

/**
 * The centre's caseload.
 *
 * Names, who treats them, how much work has happened and when it last did.
 * Nothing else is here and nothing else can be: an administrator distributes
 * work, they do not read anybody's therapy.
 */
export default function CenterPatientsPage() {
    const { t } = useTranslation();
    const { center, loading } = useCenter();
    const [patientFilter, setPatientFilter] = useState("");
    const [therapistFilter, setTherapistFilter] = useState("");
    // A training centre's students, by course and by year of it.
    const isTraining = center?.type === "training";
    const [courseFilter, setCourseFilter] = useState("");
    const [yearFilter, setYearFilter] = useState("");
    // The list is split by kind, each with its own filters.
    const [tab, setTab] = useState<ListTab>("individual");
    const { modes: sessionModes } = useSessionPreferences();
    // In the couple and group tabs: one of them, picked by name.
    const [unitPick, setUnitPick] = useState("");
    const { courses } = useCenterCourses(center?.id, isTraining);
    const yearOptions = academicYearOptions(new Date(), yearFilter ? Number(yearFilter) : null);
    // The staff's name for a course, not the one its students read.
    const courseName = (id?: string | null) => courses.find((c) => c.id === id)?.internal_name;
    const [addingStudents, setAddingStudents] = useState(false);
    const [importingStudents, setImportingStudents] = useState(false);
    // Sessions held before Alchely, brought in so the reports count them.
    const [importingHistory, setImportingHistory] = useState(false);
    // The patient whose details are open, if any.
    const [viewing, setViewing] = useState<string | null>(null);
    // The group being edited, if any.
    const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
    const [editingCoupleId, setEditingCoupleId] = useState<string | null>(null);
    const {
        patients: listed,
        total,
        loading: listLoading,
    } = useCenterPatients(center?.id, {
        therapist_id: therapistFilter || undefined,
        course_id: courseFilter || undefined,
        academic_year: yearFilter ? Number(yearFilter) : undefined,
        // All of it: records are grouped into people and filtered here, which
        // a page from the server would split. The table pages what results.
        limit: WHOLE_CASELOAD,
    });
    // Everyone who has treated here, for names and the filter — a suspended
    // member's patients still need a name beside them. Only practising members
    // can be handed a patient.
    const { couples, groups, loading: unitsLoading } = useCenterUnits(center?.id, {
        therapist_id: therapistFilter || undefined,
    });
    const { members: everyone } = useCenterMembers(center?.id);
    const { members } = useCenterMembers(center?.id, true);
    const reassign = useReassignPatient(center?.id);
    const reassignUnit = useReassignUnit(center?.id);

    const [moving, setMoving] = useState<{ kind: SubjectKind; id: string } | null>(null);
    const [target, setTarget] = useState("");
    const [reason, setReason] = useState("");
    const [error, setError] = useState<string | null>(null);
    // Said once a handover went through: who was emailed about it.
    const [notice, setNotice] = useState<string | null>(null);
    // What the handover would do to the bookings still to come, asked once a
    // destination is picked. Warnings only: confirming stays possible.
    const { preview } = useReassignmentPreview(
        center?.id,
        moving && target ? { kind: moving.kind, id: moving.id, toTherapistId: target } : null
    );

    // A student's course and year, under their name.
    const studyDetail = (patient: CenterPatient) =>
        isTraining && patient.course_id
            ? [
                  courseName(patient.course_id),
                  patient.academic_year ? formatAcademicYear(patient.academic_year) : null,
              ]
                  .filter(Boolean)
                  .join(" · ")
            : undefined;

    // One entry per person, not per record: the centre may hold two records
    // for somebody seen by two of its professionals, and offering their name
    // twice asks the administrator to pick between two identical lines.
    const people = groupByPerson(listed);
    const recordIdsByPerson = new Map(
        people.map((person) => [person.key, new Set(person.records.map((r) => r.patient_id))]),
    );
    const patientOptions = people.map((person) => ({
        value: person.key,
        label: `${person.firstName} ${person.lastName}`,
    }));

    // One row per person, not per record: a centre holds a record per
    // therapeutic relationship, so somebody seen by two of its professionals
    // was appearing twice, as if they were two people who share a name.
    const memberNames = (list: CenterPatient[]) => list.map((m) => `${m.first_name} ${m.last_name}`).join(", ");
    const rows: CaseloadRow[] = [
        ...(tab === "individual" ? people : []).map((person) => {
            const [first, ...rest] = person.records;
            return {
                kind: "individual" as const,
                id: first.patient_id,
                name: `${person.firstName} ${person.lastName}`,
                detail: studyDetail(first),
                email: person.email,
                therapistId: first.therapist_id,
                sessionCount: first.session_count,
                lastSessionAt: first.last_session_at,
                memberIds: person.records.map((record) => record.patient_id),
                alsoWith: rest.map((record) => ({
                    patientId: record.patient_id,
                    therapistId: record.therapist_id,
                    sessionCount: record.session_count,
                    lastSessionAt: record.last_session_at,
                    detail: studyDetail(record),
                })),
            };
        }),
        ...(tab === "couple" ? couples : []).map((couple) => ({
            kind: "couple" as const,
            id: couple.couple_id,
            name:
                couple.display_name ||
                couple.members.map((m) => `${m.first_name} ${m.last_name}`).join(" & "),
            // Who is in it, under its name — the centre's view of its members.
            detail: couple.display_name ? memberNames(couple.members) : undefined,
            therapistId: couple.therapist_id,
            sessionCount: couple.session_count ?? 0,
            lastSessionAt: couple.last_session_at ?? null,
            memberIds: couple.members.map((m) => m.patient_id),
        })),
        ...(tab === "group" ? groups : []).map((group) => ({
            kind: "group" as const,
            id: group.group_id,
            // Its given name or its size — its members are listed under it.
            name: centerGroupLabel(t, group.display_name, group.members.length),
            detail: memberNames(group.members),
            therapistId: group.therapist_id,
            sessionCount: group.session_count ?? 0,
            lastSessionAt: group.last_session_at ?? null,
            memberIds: group.members.map((m) => m.patient_id),
        })),
    ].sort((a, b) => a.name.localeCompare(b.name));

    // The patient filter picks one person — every record the centre holds for
    // them, and the couples and groups they are in — out of what the therapist
    // filter left.
    const filteredIds = patientFilter ? recordIdsByPerson.get(patientFilter) : undefined;
    const shown =
        tab !== "individual"
            ? rows.filter((row) => !unitPick || row.id === unitPick)
            : filteredIds
              ? rows.filter((row) => row.memberIds.some((id) => filteredIds.has(id)))
              : rows;
    // Any change of tab or filter starts the list over from its first page;
    // a page emptied by a handover falls back to the last one left.
    const [offset, setOffset] = useState(0);
    useEffect(() => {
        setOffset(0);
    }, [tab, patientFilter, therapistFilter, courseFilter, yearFilter, unitPick]);
    const pageStart =
        offset < shown.length ? offset : Math.max(0, Math.floor((shown.length - 1) / PAGE_SIZE) * PAGE_SIZE);
    const pageRows = shown.slice(pageStart, pageStart + PAGE_SIZE);
    const unitOptions = rows.map((row) => ({ value: row.id, label: row.name, subLabel: row.detail }));
    const movingRow = moving ? rows.find((row) => row.kind === moving.kind && row.id === moving.id) : undefined;
    const therapistOptions = everyone
        .filter((member) => member.roles.includes("therapist"))
        .map((member) => ({
            value: member.therapist_id,
            label: `${member.first_name} ${member.last_name}`,
        }));

    const therapistName = (id: string) => {
        const member = everyone.find((m) => m.therapist_id === id);
        return member ? `${member.first_name} ${member.last_name}` : "—";
    };

    const clearFilters = () => {
        setPatientFilter("");
        setTherapistFilter("");
        setCourseFilter("");
        setYearFilter("");
        setUnitPick("");
    };

    const switchTab = (next: ListTab) => {
        setTab(next);
        setUnitPick("");
        setPatientFilter("");
        setMoving(null);
    };
    // A school lists couples only if it has any; a therapy centre always offers them.
    // The people themselves are always listed; couples and groups only where
    // the centre runs them.
    const tabs: ListTab[] = (
        isTraining && couples.length === 0 ? ["individual", "group"] : ["individual", "couple", "group"]
    ).filter((kind): kind is ListTab => kind === "individual" || sessionModes[kind as "couple" | "group"]);
    const tabCount = { individual: people.length, couple: couples.length, group: groups.length };

    const closeReassign = () => {
        setMoving(null);
        setTarget("");
        setReason("");
        setError(null);
    };

    const handleReassign = async (row: { kind: SubjectKind; id: string }) => {
        setError(null);
        setNotice(null);
        const input = { toTherapistId: target, reason: reason.trim() || undefined };
        // Read before the panel closes: the server writes to everybody at each
        // booking that moved, so there is only something to say if some did.
        const emailed = (preview?.future_appointments ?? 0) > 0;
        const destination = therapistName(target);
        try {
            if (row.kind === "individual") {
                await reassign.mutateAsync({ patientId: row.id, ...input });
            } else {
                // A couple or a group moves whole: every member goes with it.
                await reassignUnit.mutateAsync({ kind: row.kind, unitId: row.id, ...input });
            }
            setMoving(null);
            setTarget("");
            setReason("");
            if (emailed) setNotice(t("center.patients.emailed", { therapist: destination }));
        } catch (err: unknown) {
            const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data
                ?.detail;
            setError(t(REASSIGN_ERRORS[detail ?? ""] ?? "center.patients.errorGeneric"));
        }
    };
    const pending = reassign.isPending || reassignUnit.isPending;

    if (loading || !center) {
        return <p className="p-6 text-sm text-gray-500">{t("common.loading")}</p>;
    }

    return (
        <CenterAdminShell center={center}>
            <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-semibold text-content-primary">
                            {t("center.patients.title")}
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t("center.patients.subtitle")}
                        </p>
                    </div>
                    {/* A centre takes its people on itself: a school its students, in
                        their course; a therapy centre its patients, groups and couples. */}
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setImportingHistory(true)}
                            startIcon={<History size={16} aria-hidden="true" />}
                        >
                            {t("pastSessions.open")}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setImportingStudents(true)}
                            startIcon={<FileUp size={16} aria-hidden="true" />}
                        >
                            {t("center.csv.students.button")}
                        </Button>
                        <Button
                            onClick={() => setAddingStudents(true)}
                            startIcon={<UserPlus size={16} aria-hidden="true" />}
                        >
                            {t("center.students.button")}
                        </Button>
                    </div>
                </div>

                <div role="tablist" aria-label={t("center.patients.title")} className="mb-5 flex flex-wrap gap-1 border-b border-gray-200 dark:border-gray-800">
                    {tabs.map((kind) => (
                        <button
                            key={kind}
                            type="button"
                            role="tab"
                            aria-selected={tab === kind}
                            onClick={() => switchTab(kind)}
                            className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                                tab === kind
                                    ? "border-brand-600 text-brand-700 dark:border-brand-300 dark:text-brand-300"
                                    : "border-transparent text-gray-500 hover:text-content-primary dark:text-gray-400"
                            }`}
                        >
                            {t(`center.patients.tabs.${kind}`)}
                            <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs tabular-nums text-gray-600 dark:bg-white/5 dark:text-gray-400">
                                {tabCount[kind]}
                            </span>
                        </button>
                    ))}
                </div>

                {/* Each list its own filters, in a grid that wraps instead of running off the card. */}
                <div className="mb-6 grid grid-cols-1 items-end gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {tab === "individual" ? (
                        <div className="min-w-0">
                            <Label>{t("center.patients.filterPatient")}</Label>
                            <FilterSelect
                                options={patientOptions}
                                value={patientFilter}
                                onChange={(value) => setPatientFilter(value as string)}
                                placeholder={
                                    listLoading ? t("common.loading") : t("center.patients.searchPatient")
                                }
                                searchPlaceholder={t("center.patients.searchPatient")}
                                className="border-border-strong bg-transparent text-sm"
                            />
                        </div>
                    ) : (
                        <div className="min-w-0">
                            <Label>{t(`center.patients.filterName.${tab}`)}</Label>
                            <FilterSelect
                                options={unitOptions}
                                value={unitPick}
                                onChange={(value) => setUnitPick((value as string) || "")}
                                placeholder={t(`center.patients.searchName.${tab}`)}
                                searchPlaceholder={t(`center.patients.searchName.${tab}`)}
                                className="border-border-strong bg-transparent text-sm"
                            />
                        </div>
                    )}
                    <div className="min-w-0">
                        <Label>{t("center.patients.filterTherapist")}</Label>
                        <FilterSelect
                            options={therapistOptions}
                            value={therapistFilter}
                            onChange={(value) => {
                                setTherapistFilter(value as string);
                                // One picked under another therapist would silently match nothing.
                                setPatientFilter("");
                                setUnitPick("");
                            }}
                            placeholder={t("center.patients.searchTherapist")}
                            searchPlaceholder={t("center.patients.searchTherapist")}
                            icon={<UserRound className="h-5 w-5" />}
                            className="border-border-strong bg-transparent text-sm"
                        />
                    </div>
                    {isTraining && tab === "individual" && (
                        <>
                            <div className="min-w-0">
                                <Label>{t("patient.course.course")}</Label>
                                <FilterSelect
                                    options={courses.map((course) => ({
                                        value: course.id,
                                        label: course.internal_name,
                                    }))}
                                    value={courseFilter}
                                    onChange={(value) => {
                                        setCourseFilter((value as string) || "");
                                        setPatientFilter("");
                                    }}
                                    placeholder={t("patient.course.anyCourse")}
                                    searchPlaceholder={t("patient.searchPlaceholder")}
                                    className="border-border-strong bg-transparent text-sm"
                                />
                            </div>
                            <div className="min-w-0">
                                <Label>{t("patient.course.year")}</Label>
                                <FilterSelect
                                    options={yearOptions}
                                    value={yearFilter}
                                    onChange={(value) => {
                                        setYearFilter((value as string) || "");
                                        setPatientFilter("");
                                    }}
                                    placeholder={t("patient.course.anyYear")}
                                    searchPlaceholder={t("patient.searchPlaceholder")}
                                    className="border-border-strong bg-transparent text-sm"
                                />
                            </div>
                        </>
                    )}
                    <Button
                        variant="outline"
                        className="w-full sm:w-auto sm:justify-self-start"
                        onClick={clearFilters}
                        disabled={!patientFilter && !therapistFilter && !courseFilter && !yearFilter && !unitPick}
                    >
                        {t("common.clear")}
                    </Button>
                </div>

                {(listLoading || unitsLoading) && (
                    <p className="text-sm text-gray-500">{t("common.loading")}</p>
                )}

                {!listLoading && !unitsLoading && shown.length === 0 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t(tab === "individual" ? "center.patients.empty" : `center.patients.emptyUnits.${tab}`)}
                    </p>
                )}

                {!listLoading && !unitsLoading && shown.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500 dark:border-gray-800 dark:text-gray-400">
                                    <th scope="col" className="w-12 py-2 pr-3 font-medium">
                                        <span className="sr-only">{t("center.patients.type")}</span>
                                    </th>
                                    <th scope="col" className="py-2 pr-4 font-medium">
                                        {t("center.patients.name")}
                                    </th>
                                    {isTraining && tab === "individual" && (
                                        <th scope="col" className="py-2 pr-4 font-medium">
                                            {t("center.patients.course")}
                                        </th>
                                    )}
                                    <th scope="col" className="py-2 pr-4 font-medium">
                                        {t("center.patients.therapist")}
                                    </th>
                                    <th scope="col" className="py-2 pr-4 font-medium">
                                        {t("center.patients.sessions")}
                                    </th>
                                    <th scope="col" className="py-2 pr-4 font-medium">
                                        {t("center.patients.lastSession")}
                                    </th>
                                    {/* Pinned to the right edge: with a training centre's
                                        course column the table outgrows the panel, and
                                        macOS hides the scrollbar that would reveal it. */}
                                    <th scope="col" className={`${ACTION_CELL} py-2 font-medium`} />
                                </tr>
                            </thead>
                            <tbody>
                                {pageRows.map((row) => (
                                    <Fragment key={`${row.kind}:${row.id}`}>
                                        <tr className="border-b border-gray-100 last:border-0 dark:border-gray-800">
                                        <td className="py-3 pr-3 text-content-tertiary">
                                            {/* One person, two, several — the same marks as the new-session picker. */}
                                            <span
                                                role="img"
                                                aria-label={t(`center.patients.kinds.${row.kind}`)}
                                                title={t(`center.patients.kinds.${row.kind}`)}
                                                className="inline-flex"
                                            >
                                                <SubjectKindIcon kind={row.kind} />
                                            </span>
                                        </td>
                                        <td className="py-3 pr-4 text-content-primary">
                                            {row.kind === "individual" ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setViewing(row.id)}
                                                    aria-label={t("center.patients.detail.open", { name: row.name })}
                                                    className="text-left font-medium text-content-primary underline-offset-2 hover:text-brand-600 hover:underline dark:hover:text-brand-300"
                                                >
                                                    {row.name}
                                                </button>
                                            ) : row.kind === "couple" ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setEditingCoupleId(row.id)}
                                                    aria-label={t("center.couples.editNamed", { name: row.name })}
                                                    className="text-left font-medium text-content-primary underline-offset-2 hover:text-brand-600 hover:underline dark:hover:text-brand-300"
                                                >
                                                    {row.name}
                                                </button>
                                            ) : row.kind === "group" ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setEditingGroupId(row.id)}
                                                    aria-label={t("center.groups.editNamed", { name: row.name })}
                                                    className="text-left font-medium text-content-primary underline-offset-2 hover:text-brand-600 hover:underline dark:hover:text-brand-300"
                                                >
                                                    {row.name}
                                                </button>
                                            ) : (
                                                row.name
                                            )}
                                            {row.email && (
                                                <span className="block text-xs text-gray-500 [overflow-wrap:anywhere] dark:text-gray-400">
                                                    {row.email}
                                                </span>
                                            )}
                                            {row.kind !== "individual" && row.detail && (
                                                <span className="block text-xs text-gray-500 dark:text-gray-400">
                                                    {row.detail}
                                                </span>
                                            )}
                                        </td>
                                        {isTraining && tab === "individual" && (
                                            <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                                                {row.detail || "—"}
                                            </td>
                                        )}
                                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                                            {therapistName(row.therapistId)}
                                        </td>
                                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                                            {row.sessionCount}
                                        </td>
                                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                                            {row.lastSessionAt
                                                ? new Date(row.lastSessionAt).toLocaleDateString()
                                                : "—"}
                                        </td>
                                        <td className={`${ACTION_CELL} py-3 text-right`}>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => {
                                                    setError(null);
                                                    setNotice(null);
                                                    setMoving(
                                                        moving?.kind === row.kind && moving.id === row.id
                                                            ? null
                                                            : { kind: row.kind, id: row.id }
                                                    );
                                                }}
                                            >
                                                {t("center.patients.reassign")}
                                            </Button>
                                        </td>
                                        </tr>
                                        {/* The same person, with another of the
                                            centre's professionals. Its own line,
                                            with its own count: separate
                                            therapies, never one total. */}
                                        {(row.alsoWith ?? []).map((other) => (
                                            <tr
                                                key={`also:${other.patientId}`}
                                                className="border-b border-gray-100 last:border-0 dark:border-gray-800"
                                            >
                                                <td className="py-3 pr-3" />
                                                <td className="py-3 pr-4 pl-4 text-xs text-gray-500 dark:text-gray-400">
                                                    {t("center.patients.alsoWith")}
                                                </td>
                                                {isTraining && tab === "individual" && (
                                                    <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                                                        {other.detail || "—"}
                                                    </td>
                                                )}
                                                <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                                                    {therapistName(other.therapistId)}
                                                </td>
                                                <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                                                    {other.sessionCount}
                                                </td>
                                                <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                                                    {other.lastSessionAt
                                                        ? new Date(other.lastSessionAt).toLocaleDateString()
                                                        : "—"}
                                                </td>
                                                <td className={`${ACTION_CELL} py-3 text-right`}>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => {
                                                            setError(null);
                                                            setNotice(null);
                                                            setMoving(
                                                                moving?.kind === "individual" &&
                                                                    moving.id === other.patientId
                                                                    ? null
                                                                    : { kind: "individual", id: other.patientId }
                                                            );
                                                        }}
                                                    >
                                                        {t("center.patients.reassign")}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                        </Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* A dialog over the list, not a panel under it: under a page of
                    rows it opened out of sight, and the button looked dead. */}
                <Modal
                    isOpen={Boolean(moving)}
                    onClose={closeReassign}
                    ariaLabel={t("center.patients.reassign")}
                    className="mx-4 max-w-xl rounded-3xl"
                >
                {moving && (
                    <div className="p-6">
                        <h3 className="mb-1 text-base font-semibold text-content-primary">
                            {moving.kind === "individual"
                                ? t("center.patients.reassignTitle")
                                : t(`center.patients.reassignUnitTitle_${moving.kind}`)}
                            {movingRow && `: ${movingRow.name}`}
                        </h3>
                        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                            {moving.kind === "individual"
                                ? t("center.patients.reassignHint")
                                : t("center.patients.reassignUnitHint")}
                        </p>
                        <div className="flex flex-wrap items-end gap-3">
                            <div>
                                <Label htmlFor="reassign-target">
                                    {t("center.patients.newTherapist")}
                                </Label>
                                <select
                                    id="reassign-target"
                                    value={target}
                                    onChange={(e) => setTarget(e.target.value)}
                                    className="h-11 rounded-lg border border-gray-300 bg-transparent px-3 text-sm dark:border-gray-700"
                                >
                                    <option value="">{t("center.patients.choose")}</option>
                                    {members.map((member) => (
                                        <option key={member.therapist_id} value={member.therapist_id}>
                                            {member.first_name} {member.last_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="min-w-[16rem] flex-1">
                                <Label htmlFor="reassign-reason">
                                    {t("center.patients.reason")}
                                </Label>
                                <Input
                                    id="reassign-reason"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                />
                            </div>
                        </div>
                        {/* Above the button, so it is read before confirming. */}
                        {target && preview && (
                            <ReassignmentWarnings
                                preview={preview}
                                therapistName={therapistName(target)}
                            />
                        )}
                        <div className="mt-4 flex justify-end gap-2">
                            <Button variant="outline" onClick={closeReassign} disabled={pending}>
                                {t("common.cancel")}
                            </Button>
                            <Button
                                onClick={() => handleReassign(moving)}
                                disabled={!target || pending}
                                loading={pending}
                            >
                                {t("center.patients.confirmReassign")}
                            </Button>
                        </div>
                        {error && (
                            <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">
                                {error}
                            </p>
                        )}
                    </div>
                )}
                </Modal>

                {!listLoading && !unitsLoading && (
                    <Pagination
                        offset={pageStart}
                        total={shown.length}
                        pageSize={PAGE_SIZE}
                        onPrev={() => setOffset(Math.max(pageStart - PAGE_SIZE, 0))}
                        onNext={() => setOffset(pageStart + PAGE_SIZE)}
                    />
                )}

                {notice && (
                    <p role="status" className="mt-4 text-sm text-green-600 dark:text-green-400">
                        {notice}
                    </p>
                )}

                <p className="mt-4 text-xs text-content-tertiary">
                    {/* What this tab lists. The server's `total` counts records,
                        and a person the centre holds twice is still one person. */}
                    {t("center.patients.total", {
                        count: tab === "individual" ? total - (listed.length - people.length) : tabCount[tab],
                    })}
                </p>
            </section>
            {editingCoupleId && couples.some((couple) => couple.couple_id === editingCoupleId) && (
                <CenterCoupleEditModal
                    centerId={center.id}
                    couple={couples.find((couple) => couple.couple_id === editingCoupleId)!}
                    therapistName={therapistName(
                        couples.find((couple) => couple.couple_id === editingCoupleId)?.therapist_id ?? ""
                    )}
                    onClose={() => setEditingCoupleId(null)}
                />
            )}
            {editingGroupId && (
                <CenterGroupEditModal
                    centerId={center.id}
                    group={groups.find((group) => group.group_id === editingGroupId) ?? null}
                    mentorName={therapistName(
                        groups.find((group) => group.group_id === editingGroupId)?.therapist_id ?? ""
                    )}
                    onClose={() => setEditingGroupId(null)}
                />
            )}
            <CenterPatientDetailModal
                centerId={center.id}
                patientId={viewing}
                records={(people.find((person) => person.records.some((r) => r.patient_id === viewing))?.records ?? []).map(
                    (record) => ({
                        patientId: record.patient_id,
                        therapistId: record.therapist_id,
                        courseId: record.course_id,
                    })
                )}
                onClose={() => setViewing(null)}
                isTraining={isTraining}
                courses={courses}
                therapistName={therapistName}
            />
            <StudentImportModal
                isOpen={importingStudents}
                onClose={() => setImportingStudents(false)}
                centerId={center.id}
                isTraining={isTraining}
            />
            {importingHistory && (
                <CenterPastSessionsImport
                    centerId={center.id}
                    onClose={() => setImportingHistory(false)}
                    courses={isTraining ? courses : undefined}
                />
            )}
            <AddStudentsModal
                isOpen={addingStudents}
                onClose={() => setAddingStudents(false)}
                centerId={center.id}
                isTraining={isTraining}
            />
        </CenterAdminShell>
    );
}
